import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    EnvironmentProviders,
    Type,
    inject,
    makeEnvironmentProviders,
    provideEnvironmentInitializer
} from '@angular/core';
import { filter, map, take } from 'rxjs';

import { GuardRegistryService } from '@mobi/rwc-utils-common-ng-jslib';
import { TelemetryUserContext } from '@mobi/rwc-utils-observability-ng-jslib';

import { ImpersonationService } from '../impersonation/impersonation.service';
import { BpsImpersonationService } from '../impersonation/internal/bps-impersonation.service';
import { impersonationInterceptor } from '../impersonation/internal/impersonation.interceptor';

import { ACCESS_TOKEN_PROVIDER, AccessTokenProvider } from './access-token.provider';
import { AppServiceAuthService } from './app-service/app-service-auth.service';
import { AUTH_INFO_PROVIDER } from './auth-provider';
import { accessTokenInterceptor } from './internal/access-token.interceptor';
import { AppServiceUserInfoService } from './user-info/app-service-user-info.service';
import { UserInfoService } from './user-info/user-info.service';

export interface AzureAuthOptions {
    accessTokenProvider?: Type<AccessTokenProvider>;
}

export function provideAzureAuth(options: AzureAuthOptions = {}): EnvironmentProviders {
    return makeEnvironmentProviders([
        { provide: ACCESS_TOKEN_PROVIDER, useClass: options.accessTokenProvider ?? AppServiceAuthService },
        { provide: AUTH_INFO_PROVIDER, useValue: 'AppService' as const },
        { provide: UserInfoService, useClass: AppServiceUserInfoService },
        { provide: ImpersonationService, useClass: BpsImpersonationService },
        provideHttpClient(withInterceptors([accessTokenInterceptor, impersonationInterceptor])),
        provideEnvironmentInitializer(() => {
            // Ensure the service subscribes to router events as early as possible, to avoid missing NavigationStart event.
            return inject(ImpersonationService).onBehalfOf;
        }),
        provideEnvironmentInitializer(() => {
            const userContext = inject(TelemetryUserContext);

            inject(UserInfoService)
                .loggedInUser.pipe(
                    map(userinfo => userinfo.userId),
                    filter(Boolean),
                    take(1)
                )
                .subscribe(userId => {
                    userContext.setAuthenticatedUser(userId);
                });

            // for webComponent: They need to know, in a sync way, if the host uses CIP or AppService
            const config = window.document.defaultView?.['MOBI_RWC_CONFIG'] as Record<string, string>;
            if (config) {
                config['isAppServiceAuth'] = 'true';
            }

            inject(GuardRegistryService).checkAzureAuthGuard();

            // Ensure the service subscribes to router events as early as possible, to avoid missing NavigationStart event.
            return inject(ImpersonationService).onBehalfOf;
        })
    ]);
}
